/**
 * TemplateEditor — editor de templates com 4 modos:
 *   - Visual    : WYSIWYG num iframe com body contentEditable (VisualFrame),
 *                 com toolbar (Parágrafo/H1/H2, B/I/U, listas, link, tabela,
 *                 imagem) via execCommand no documento do iframe.
 *   - Blocos    : construtor arrasta-e-solta (GrapesJS, lazy).
 *   - Código HTML: textarea monospace com o HTML-fonte (fiel ao valor).
 *   - Pré-visualização: HtmlTemplateFrame com o HTML renderizado pelo backend
 *                 (dados de exemplo) — MESMO componente da página pública, o
 *                 que o gestor vê aqui é o que o cliente vai ver.
 *
 * Todos os modos leem/gravam o MESMO campo `html` através do contrato de
 * forma em ./templateHtml (parse/serialize) — alternar de aba não muda a
 * forma persistida (fragmento ou documento completo).
 *
 * Botão "Variável" abre um popover com o catálogo (GET /variaveis) e insere
 * `{{chave}}` (ou o bloco `{% for %}`) no cursor. Mantém os tokens Jinja como
 * texto puro no HTML (o que o backend renderiza).
 *
 * Props: { value, onChange, tipo, request, height }.
 */
import React, { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";
import {
  FiBold,
  FiChevronsRight,
  FiCode,
  FiEye,
  FiGrid,
  FiImage,
  FiItalic,
  FiLink,
  FiList,
  FiUnderline,
} from "react-icons/fi";

import {
  obterVariaveisTemplate,
  previewTemplate,
} from "../../services/templatesOrcamento";
import HtmlTemplateFrame from "../HtmlTemplateFrame";
import VisualFrame from "./VisualFrame";

// GrapesJS (construtor arrasta-e-solta) é pesado — só carrega ao abrir "Blocos".
const GrapesEditor = lazy(() => import("../GrapesEditor"));

const MODOS = [
  { id: "visual", label: "Visual" },
  { id: "blocos", label: "Blocos" },
  { id: "html", label: "Código HTML" },
  { id: "preview", label: "Pré-visualização" },
];

const TABELA_HTML =
  '<table border="1" cellpadding="6" style="border-collapse:collapse;width:100%"><thead><tr><th>Coluna 1</th><th>Coluna 2</th></tr></thead><tbody><tr><td>—</td><td>—</td></tr></tbody></table><p></p>';

export default function TemplateEditor({
  value,
  onChange,
  tipo,
  request,
  height = 460,
  modo: modoProp,
  onModoChange,
}) {
  const [modoInterno, setModoInterno] = useState("visual");
  const modo = modoProp ?? modoInterno;
  const setModo = onModoChange ?? setModoInterno;
  const visualApiRef = useRef(null);
  const htmlRef = useRef(null);
  const imgInputRef = useRef(null);
  const [grupos, setGrupos] = useState([]);
  const [varAberto, setVarAberto] = useState(false);
  const varWrapRef = useRef(null);
  const [preview, setPreview] = useState({ loading: false, html: "", erro: null });

  // Catálogo de variáveis (uma vez)
  useEffect(() => {
    let vivo = true;
    obterVariaveisTemplate(request)
      .then((d) => {
        if (vivo) setGrupos(d?.grupos || []);
      })
      .catch(() => {
        /* sem catálogo: botão Variável fica vazio */
      });
    return () => {
      vivo = false;
    };
  }, [request]);

  // Carrega a pré-visualização ao entrar no modo (ou quando o html muda)
  useEffect(() => {
    if (modo !== "preview") return undefined;
    let vivo = true;
    setPreview((p) => ({ ...p, loading: true, erro: null }));
    previewTemplate(request, { html: value, tipo })
      .then((d) => {
        if (vivo) setPreview({ loading: false, html: d?.html_renderizado || "", erro: null });
      })
      .catch((err) => {
        if (vivo)
          setPreview({
            loading: false,
            html: "",
            erro: err?.message || "Erro ao renderizar o template.",
          });
      });
    return () => {
      vivo = false;
    };
  }, [modo, value, tipo, request]);

  // Fecha o popover de variáveis ao clicar fora
  useEffect(() => {
    if (!varAberto) return undefined;
    const onDoc = (e) => {
      if (!varWrapRef.current?.contains(e.target)) setVarAberto(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [varAberto]);

  // Toolbar do Visual: delega ao documento do iframe (VisualFrame), que
  // sincroniza o valor internamente preservando a forma do documento.
  const cmd = useCallback((command, arg) => {
    visualApiRef.current?.exec(command, arg);
  }, []);

  const inserirNoHtml = useCallback(
    (texto) => {
      const ta = htmlRef.current;
      if (!ta) {
        onChange?.((value || "") + texto);
        return;
      }
      const start = ta.selectionStart ?? (value || "").length;
      const end = ta.selectionEnd ?? start;
      const novo = (value || "").slice(0, start) + texto + (value || "").slice(end);
      onChange?.(novo);
      requestAnimationFrame(() => {
        ta.focus();
        const pos = start + texto.length;
        ta.selectionStart = ta.selectionEnd = pos;
      });
    },
    [value, onChange]
  );

  const inserirTexto = useCallback(
    (texto) => {
      if (modo === "html") {
        inserirNoHtml(texto);
      } else {
        visualApiRef.current?.insertText(texto);
      }
    },
    [modo, inserirNoHtml]
  );

  const inserirVariavel = useCallback(
    (chave) => {
      inserirTexto(`{{${chave}}}`);
      setVarAberto(false);
    },
    [inserirTexto]
  );

  const inserirLoop = useCallback(
    (loop) => {
      inserirTexto(`\n${loop.abre}\n  \n${loop.fecha}\n`);
      setVarAberto(false);
    },
    [inserirTexto]
  );

  const onHeading = useCallback(
    (e) => {
      const v = e.target.value;
      const tag = v === "h1" ? "<h1>" : v === "h2" ? "<h2>" : "<p>";
      cmd("formatBlock", tag);
      e.target.value = "p";
    },
    [cmd]
  );

  const onLink = useCallback(() => {
    const url = window.prompt("URL do link:", "https://");
    if (url) cmd("createLink", url);
  }, [cmd]);

  // Imagem: upload do computador (embute como data URL base64 — sem depender de
  // storage/CDN; funciona offline e em PDF/e-mail). Para imagens muito grandes,
  // o ideal futuro e um endpoint de upload que devolve URL.
  const onImage = useCallback(() => {
    imgInputRef.current?.click();
  }, []);

  const onImageFile = useCallback(
    (e) => {
      const file = e.target.files && e.target.files[0];
      e.target.value = ""; // permite re-selecionar o mesmo arquivo
      if (!file) return;
      if (!file.type.startsWith("image/")) {
        window.alert("Selecione um arquivo de imagem.");
        return;
      }
      if (file.size > 4 * 1024 * 1024) {
        window.alert("Imagem muito grande (máx. 4 MB). Otimize antes de inserir.");
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        cmd(
          "insertHTML",
          `<img src="${reader.result}" alt="" style="max-width:100%" />`
        );
      };
      reader.readAsDataURL(file);
    },
    [cmd]
  );

  const podeFormatar = modo === "visual";

  return (
    <div className="wysiwyg" style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 360 }}>
      {/* Input de upload de imagem (oculto) — acionado pelo botao Imagem */}
      <input
        ref={imgInputRef}
        type="file"
        accept="image/*"
        style={{ display: "none" }}
        onChange={onImageFile}
      />
      {/* Tabs de modo + toolbar */}
      <div className="wysiwyg__toolbar" style={{ justifyContent: "space-between" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
          <div className="tabchip-group" style={{ height: 26, marginRight: 6 }}>
            {MODOS.map((m) => (
              <div
                key={m.id}
                role="button"
                tabIndex={0}
                className={`tabchip${modo === m.id ? " active" : ""}`}
                onClick={() => setModo(m.id)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setModo(m.id);
                  }
                }}
              >
                {m.id === "preview" ? <FiEye size={12} style={{ marginRight: 4 }} /> : null}
                {m.id === "html" ? <FiCode size={12} style={{ marginRight: 4 }} /> : null}
                {m.id === "blocos" ? <FiGrid size={12} style={{ marginRight: 4 }} /> : null}
                {m.label}
              </div>
            ))}
          </div>

          {podeFormatar && (
            <>
              <span className="wysiwyg__sep" />
              <select
                className="wysiwyg__select"
                defaultValue="p"
                onChange={onHeading}
                title="Formato do parágrafo"
              >
                <option value="p">Parágrafo</option>
                <option value="h1">Título 1</option>
                <option value="h2">Título 2</option>
              </select>
              <span className="wysiwyg__sep" />
              <button type="button" className="wysiwyg__tool" title="Negrito" onClick={() => cmd("bold")}>
                <FiBold size={15} />
              </button>
              <button type="button" className="wysiwyg__tool" title="Itálico" onClick={() => cmd("italic")}>
                <FiItalic size={15} />
              </button>
              <button type="button" className="wysiwyg__tool" title="Sublinhado" onClick={() => cmd("underline")}>
                <FiUnderline size={15} />
              </button>
              <span className="wysiwyg__sep" />
              <button type="button" className="wysiwyg__tool" title="Lista" onClick={() => cmd("insertUnorderedList")}>
                <FiList size={15} />
              </button>
              <button
                type="button"
                className="wysiwyg__tool"
                title="Lista numerada"
                onClick={() => cmd("insertOrderedList")}
                style={{ fontFamily: "var(--font-mono)", fontSize: 11 }}
              >
                1.
              </button>
              <span className="wysiwyg__sep" />
              <button type="button" className="wysiwyg__tool" title="Link" onClick={onLink}>
                <FiLink size={15} />
              </button>
              <button type="button" className="wysiwyg__tool" title="Tabela" onClick={() => cmd("insertHTML", TABELA_HTML)}>
                <FiGrid size={15} />
              </button>
              <button type="button" className="wysiwyg__tool" title="Imagem" onClick={onImage}>
                <FiImage size={15} />
              </button>
            </>
          )}
        </div>

        {/* Botão Variável (Visual + HTML; em Blocos as variáveis são blocos) */}
        {(modo === "visual" || modo === "html") && (
          <div ref={varWrapRef} style={{ position: "relative" }}>
            <button
              type="button"
              className="wysiwyg__tool"
              style={{ width: "auto", padding: "0 8px", fontSize: 11, fontFamily: "var(--font-mono)", gap: 4 }}
              onClick={() => setVarAberto((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={varAberto}
              title="Inserir variável"
            >
              <FiChevronsRight size={13} /> Variável
            </button>
            {varAberto && (
              <div
                role="menu"
                style={{
                  position: "absolute",
                  right: 0,
                  top: "calc(100% + 6px)",
                  width: 280,
                  maxHeight: 360,
                  overflowY: "auto",
                  background: "var(--surface)",
                  border: "1px solid var(--border)",
                  borderRadius: "var(--radius-md)",
                  boxShadow: "0 12px 32px rgba(0,0,0,0.22)",
                  padding: 8,
                  zIndex: 60,
                }}
              >
                {grupos.length === 0 && (
                  <div style={{ fontSize: 12, color: "var(--ink-3)", padding: 8 }}>
                    Catálogo indisponível.
                  </div>
                )}
                {grupos.map((g) => (
                  <div key={g.grupo} style={{ marginBottom: 8 }}>
                    <div
                      style={{
                        fontSize: 10,
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                        color: "var(--ink-3)",
                        fontFamily: "var(--font-mono)",
                        padding: "4px 6px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 6,
                      }}
                    >
                      <span>{g.grupo}</span>
                      {g.loop && (
                        <button
                          type="button"
                          className="badge accent no-dot"
                          style={{ fontSize: 9, cursor: "pointer", border: 0 }}
                          onClick={() => inserirLoop(g.loop)}
                          title="Inserir bloco de repetição"
                        >
                          + loop
                        </button>
                      )}
                    </div>
                    {g.variaveis.map((v) => (
                      <button
                        key={v.chave}
                        type="button"
                        role="menuitem"
                        onClick={() => inserirVariavel(v.chave)}
                        title={v.exemplo ? `Ex.: ${v.exemplo}` : v.chave}
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-start",
                          width: "100%",
                          border: 0,
                          background: "transparent",
                          cursor: "pointer",
                          padding: "5px 8px",
                          borderRadius: "var(--radius-sm)",
                          textAlign: "left",
                        }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = "var(--surface-2)")}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                      >
                        <span style={{ fontSize: 12, color: "var(--ink)" }}>{v.label}</span>
                        <span className="mono" style={{ fontSize: 10.5, color: "var(--accent)" }}>
                          {`{{${v.chave}}}`}
                        </span>
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Corpo por modo */}
      {modo === "visual" && (
        <VisualFrame ref={visualApiRef} value={value} onChange={onChange} />
      )}

      {modo === "blocos" && (
        <Suspense
          fallback={
            <div style={{ flex: 1, minHeight: height, padding: 18, color: "var(--ink-3)", fontSize: 14 }}>
              Carregando construtor…
            </div>
          }
        >
          <GrapesEditor value={value} onChange={onChange} variaveis={grupos} height={height} />
        </Suspense>
      )}

      {modo === "html" && (
        <textarea
          ref={htmlRef}
          value={value || ""}
          onChange={(e) => onChange?.(e.target.value)}
          spellCheck={false}
          style={{
            flex: 1,
            minHeight: 0,
            border: 0,
            outline: "none",
            resize: "none",
            padding: "16px 18px",
            fontFamily: "var(--font-mono)",
            fontSize: 12.5,
            lineHeight: 1.6,
            color: "var(--ink-2)",
            background: "var(--surface)",
            width: "100%",
          }}
          placeholder="<h1>Proposta — {{cliente.nome}}</h1>"
        />
      )}

      {modo === "preview" && (
        <div style={{ flex: 1, minHeight: 0, background: "var(--surface)", padding: 0, display: "flex", flexDirection: "column" }}>
          {preview.loading ? (
            <div style={{ padding: 18, color: "var(--ink-3)", fontSize: 14 }}>Renderizando…</div>
          ) : preview.erro ? (
            <div
              className="so-banner danger"
              style={{ margin: 16, fontSize: 13, whiteSpace: "pre-wrap" }}
            >
              {preview.erro}
            </div>
          ) : (
            // MESMO componente da página pública (OrcamentoPublico/
            // ContratoPublico), inclusive fullBleed: preview fiel ao que o
            // cliente vai ver — sem calha de padding que a página real
            // não tem.
            <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
              <HtmlTemplateFrame
                fullBleed
                html={preview.html}
                titulo="Pré-visualização do template"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
